import {
  CalendarOutlined,
  DollarOutlined,
  DownOutlined,
  FileTextOutlined,
  HistoryOutlined,
  PercentageOutlined,
  PictureOutlined,
  UnorderedListOutlined
} from "@ant-design/icons"
import { Dropdown, Flex, Radio, Tooltip, theme } from "antd"
import React, { useMemo } from "react"
import { useTranslation } from "react-i18next"

import { type PermissionProjectPermissions } from "../../../SettingsPermissions/types/permissions.types.ts"
import { useAppSelector } from "../../../../hooks/use-app-selector.ts"
import {
  type ProjectSectionKeys as Keys,
  getVisibleProjectTabs
} from "../../helpers/project-visible-tabs.ts"
import { useTabStripLayout } from "../../hooks/use-tab-strip-layout.ts"

export const projectViewTabs: Array<{
  icon: React.ReactNode
  label: string
  value: Keys
}> = [
  {
    icon: <CalendarOutlined />,
    label: "Milestones",
    value: "milestones"
  },
  {
    icon: <DollarOutlined />,
    label: "Finance",
    value: "finance"
  },
  {
    icon: <FileTextOutlined />,
    label: "Invoices",
    value: "invoices"
  },
  {
    icon: <UnorderedListOutlined />,
    label: "Tasks",
    value: "tasks"
  },
  {
    icon: <PercentageOutlined />,
    label: "Commissions",
    value: "commissions"
  },
  {
    icon: <PictureOutlined />,
    label: "Albums",
    value: "albums"
  },
  {
    icon: <HistoryOutlined />,
    label: "Logs",
    value: "logs"
  }
]

export type { ProjectSectionKeys } from "../../helpers/project-visible-tabs.ts"

/**
 * Shared by the real "More" button and the hidden probes that size it —
 * without the display, which differs between the two.
 */
const MORE_BUTTON_BASE =
  "flex-none items-center gap-1 rounded-[var(--ant-border-radius)] px-3 text-sm"

/** In the strip the button is a flex item of the strip's own row. */
const MORE_BUTTON_CLASS = `flex ${MORE_BUTTON_BASE}`

/**
 * A probe has to shrink-wrap its own label. `flex` would make it a
 * block-level box that fills the shrink-to-fit probe container, so every
 * probe would measure the widest label rather than its own.
 */
const MORE_PROBE_CLASS = `inline-flex ${MORE_BUTTON_BASE}`

interface ProjectViewSectionsProps {
  permissions: PermissionProjectPermissions | undefined
  activeTab: Keys | null
  onSectionChange: (value: Keys) => void
}

export function ProjectViewSectionsTabs({
  permissions,
  activeTab,
  onSectionChange
}: ProjectViewSectionsProps) {
  const { t } = useTranslation()
  const roleId = useAppSelector((state) => state.auth.userInfo?.role?.id)

  // Also the key its hidden probe is measured under, so the reserve matches
  // whatever the button will actually say.
  const moreFallbackLabel = t("projectSections.more", "More")

  const tabs = useMemo(() => {
    const visible = getVisibleProjectTabs(permissions, roleId)
    return projectViewTabs.filter((t) => visible.includes(t.value))
  }, [permissions, roleId])

  // antd's cssVar tokens are scoped to a hashed class rather than `:root`, so
  // `bg-[var(--ant-color-primary)]` resolves to nothing here. Read the token.
  const { token } = theme.useToken()

  const {
    mode,
    hiddenKeys,
    stripRef,
    probesRef,
    measuring,
    measuringCollapsed
  } = useTabStripLayout({ tabs, activeTab, moreLabel: moreFallbackLabel })

  if (!tabs?.length) return null

  // "more" is reached only after the icons have already failed to fit, so the
  // tabs it leaves on the strip stay collapsed.
  const iconOnly = mode === "icons" || mode === "more"
  // The second measuring pass has to render collapsed too, or it measures the
  // labelled widths and the collapsed rung is never reachable.
  const labelsCollapsed = iconOnly || measuringCollapsed
  const hiddenTabs = tabs.filter((tab) => hiddenKeys.has(tab.value))
  const activeIsHidden = activeTab !== null && hiddenKeys.has(activeTab)

  // With the open section inside the dropdown the group renders no checked
  // option, so the button carries that section's name instead of "More" and
  // takes the checked styling with it — §5.14's "active tab clearly
  // highlighted" still has to hold, and grey text beside a solid pill does not
  // read as "you are here".
  const moreLabel = activeIsHidden
    ? (tabs.find((tab) => tab.value === activeTab)?.label ?? moreFallbackLabel)
    : moreFallbackLabel

  return (
    <div className={"sticky top-0 z-10 bg-background overflow-x-clip"}>
      <div
        ref={stripRef}
        data-testid={"project-section-tabs"}
        data-mode={mode}
        className={`flex w-full items-stretch gap-1 bg-[var(--ant-radio-button-bg)] border border-border rounded-[9px] p-1 ${
          measuring ? "invisible" : ""
        }`}
      >
        <Radio.Group
          value={activeTab ?? undefined}
          onChange={(e) => onSectionChange(e.target.value)}
          buttonStyle="solid"
          className={"flex min-w-0 flex-1"}
        >
          {tabs.map((tab) => (
            // The options array API cannot be wrapped, so a tooltip would only
            // cover the label node and miss the button's padding — most of the
            // hit area once the labels are gone.
            <Tooltip
              key={tab.value}
              title={iconOnly ? tab.label : undefined}
              trigger={["hover", "focus", "click"]}
            >
              <Radio.Button
                value={tab.value}
                // antd's wrapper is `display: block` with `line-height: 30px`,
                // so content sits on a line box near the top rather than in the
                // middle of the 32px button — measured 9px high. Making the
                // wrapper itself a centring flex container is what fixes it.
                //
                // `hidden` and `flex` are both display utilities and this repo
                // imports Tailwind in `important` mode, so emitting both makes
                // them fight on emission order rather than on intent. Only ever
                // emit one.
                className={
                  hiddenKeys.has(tab.value)
                    ? "!hidden"
                    : `flex items-center justify-center border-none rounded-[var(--ant-border-radius)] ${
                        measuring ? "flex-none" : "flex-1"
                      }`
                }
              >
                <Flex align={"center"} justify={"center"} gap={"small"}>
                  {tab.icon}
                  {/* Kept in the DOM so the accessible name stays the section
                      name rather than the icon's ("calendar", "dollar"). */}
                  <span className={labelsCollapsed ? "sr-only" : undefined}>
                    {tab.label}
                  </span>
                </Flex>
              </Radio.Button>
            </Tooltip>
          ))}
        </Radio.Group>

        {hiddenTabs.length > 0 && (
          <Dropdown
            trigger={["click"]}
            menu={{
              selectedKeys: activeTab ? [activeTab] : [],
              items: hiddenTabs.map((tab) => ({
                key: tab.value,
                icon: tab.icon,
                label: tab.label
              })),
              onClick: ({ key }) => onSectionChange(key as Keys)
            }}
          >
            <button
              type={"button"}
              data-testid={"project-section-tabs-more"}
              data-active={activeIsHidden ? "true" : "false"}
              className={MORE_BUTTON_CLASS}
              style={
                activeIsHidden
                  ? {
                      background: token.colorPrimary,
                      color: token.colorTextLightSolid
                    }
                  : undefined
              }
            >
              {moreLabel} <DownOutlined />
            </button>
          </Dropdown>
        )}
      </div>

      {/* Off-flow so they cannot land in the strip's own measurements. */}
      {measuring && (
        <div
          ref={probesRef}
          aria-hidden
          className={"pointer-events-none invisible absolute left-0 top-0"}
        >
          {[moreFallbackLabel, ...tabs.map((tab) => tab.label)].map(
            (label) => (
              <span
                key={label}
                data-more-probe={label}
                className={MORE_PROBE_CLASS}
              >
                {label} <DownOutlined />
              </span>
            )
          )}
        </div>
      )}
    </div>
  )
}
